import * as React from 'react'
import { useId } from 'react'
import { clsx } from 'clsx'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'

import { useMergeRefs } from '../../hooks/useMergeRefs'
import { useScroll } from '../../hooks/useScroll'
import { useScrollFlash } from '../../hooks/useScrollFlash'
import { ScrollButton } from './ScrollButton'

import styles from './ScrollArea.module.css'

type ScrollAreaElement = React.ElementRef<typeof ScrollAreaPrimitive.Viewport>

type ScrollAreaProps = {
  /**
   * Additional CSS class to apply to the ScrollArea root
   */
  className?: string
  /**
   * Size of the scrollbar
   * @default medium
   */
  size?: 'small' | 'medium'
  /**
   * The orientation of the scrollbars
   * @default vertical
   */
  scrollbars?: 'vertical' | 'horizontal' | 'both'
  /**
   * Describes the nature of scrollbar visibility
   * `auto` means that scrollbars are visible when content is overflowing
   * `always` means that scrollbars are always visible regardless of whether the content is overflowing.
   * `scroll` means that scrollbars are visible when the user is scrolling
   * `hover` when the user is scrolling along its corresponding orientation and when the user is hovering over the scroll area
   * `none` means that scrollbars are never visible
   * @default auto
   */
  visibility?: 'auto' | 'always' | 'scroll' | 'hover' | 'none'
  /**
   * Content to render inside the scroll area
   */
  children?: React.ReactNode
  /**
   * Size (thickness) of the fade overlays in px or CSS units. When omitted,
   * fades are disabled.
   */
  gradientFade?: number | string
  /**
   * Show scroll buttons
   * @default false
   */
  showScrollButtons?: boolean
  /**
   * The amount to scroll by for the scroll buttons
   * @default 200
   */
  scrollButtonsAmount?: number
  /**
   * Briefly flashes the vertical scrollbar when the scroll area mounts,
   * hinting that there is more content available. Most useful with
   * `visibility="scroll"` or `visibility="hover"`, where the scrollbar is
   * otherwise hidden until the user interacts. Has no effect when vertical
   * content doesn't overflow.
   * @default false
   */
  flashOnMount?: boolean
} & React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Viewport>

const ScrollArea = React.forwardRef<ScrollAreaElement, ScrollAreaProps>(
  (
    {
      className,
      size = 'medium',
      scrollbars = 'vertical',
      visibility = 'auto',
      gradientFade,
      showScrollButtons = false,
      scrollButtonsAmount = 200,
      flashOnMount = false,
      ...props
    }: ScrollAreaProps,
    forwardedRef,
  ) => {
    const viewportRef = React.useRef<ScrollAreaElement>(null)
    const contentRef = React.useRef<HTMLDivElement>(null)

    const mergedRefs = useMergeRefs(forwardedRef, viewportRef)

    const {
      canScrollLeft,
      canScrollRight,
      scrollLeft,
      scrollRight,
      canScrollUp,
      canScrollDown,
      scrollUp,
      scrollDown,
    } = useScroll(viewportRef, {
      scrollAmount: scrollButtonsAmount,
      direction: 'both',
    })

    useScrollFlash(viewportRef, { enabled: flashOnMount })

    const viewportId = `scroll-viewport-${useId()}`

    // Calculate mask-image for gradient fades
    const getMaskImageStyle = () => {
      if (!gradientFade) return {}

      const fadeSize =
        typeof gradientFade === 'number' ? `${gradientFade}px` : gradientFade

      const showVertical = scrollbars !== 'horizontal'
      const showHorizontal = scrollbars !== 'vertical'

      const masks: string[] = []

      // Vertical masks (top and bottom)
      if (showVertical) {
        const topStop = canScrollUp ? fadeSize : '0px'
        const bottomStop = canScrollDown ? fadeSize : '0px'

        masks.push(
          `linear-gradient(to bottom, transparent 0, black ${topStop}, black calc(100% - ${bottomStop}), transparent 100%)`,
        )
      }

      // Horizontal masks (left and right)
      if (showHorizontal) {
        const leftStop = canScrollLeft ? fadeSize : '0px'
        const rightStop = canScrollRight ? fadeSize : '0px'

        masks.push(
          `linear-gradient(to right, transparent 0, black ${leftStop}, black calc(100% - ${rightStop}), transparent 100%)`,
        )
      }

      if (masks.length === 0) return {}

      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return {
        maskImage: masks.join(', '),
        WebkitMaskImage: masks.join(', '),
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in',
      } as React.CSSProperties
    }

    const scrollArea = (
      <ScrollAreaPrimitive.Root
        type={visibility === 'none' ? 'scroll' : visibility}
        scrollHideDelay={1000}
        className={clsx(styles.root, styles[size], className)}
      >
        {/*
          CRITICAL: Viewport MUST be a direct child of Root per Radix UI requirements.

          Do NOT:
          - Add wrapper divs between Root and Viewport
          - Apply inline width/height styles to Viewport (breaks Radix layout calculations)
          - Use pointer-events: none on Viewport (blocks all user interactions)

          Why: Radix ScrollArea relies on specific DOM structure for:
          - Scrollbar positioning and sizing calculations
          - Event delegation for scroll interactions
          - Accessibility features (keyboard navigation, screen reader support)
        */}
        <ScrollAreaPrimitive.Viewport
          {...props}
          ref={mergedRefs}
          className={styles.viewport}
          role="region"
          id={viewportId}
          style={getMaskImageStyle()}
        >
          <div ref={contentRef}>{props.children}</div>
        </ScrollAreaPrimitive.Viewport>

        {/*
          Overlay container pattern: Scroll buttons are placed in a sibling
          container with pointer-events: none, allowing click events to pass
          through to the viewport content.

          Individual interactive elements (scroll buttons) re-enable pointer-events
          with pointer-events: auto on their specific elements.
        */}
        {showScrollButtons && (
          <div className={styles.overlayContainer}>
            {/* Scroll buttons */}
            {canScrollLeft && (
              <ScrollButton
                direction="left"
                onClick={scrollLeft}
                aria-controls={viewportId}
                className={styles.navButton}
              />
            )}
            {canScrollUp && (
              <ScrollButton
                direction="up"
                onClick={scrollUp}
                aria-controls={viewportId}
                className={styles.navButton}
              />
            )}
            {canScrollDown && (
              <ScrollButton
                direction="down"
                onClick={scrollDown}
                aria-controls={viewportId}
                className={styles.navButton}
              />
            )}
            {canScrollRight && (
              <ScrollButton
                direction="right"
                onClick={scrollRight}
                aria-controls={viewportId}
                className={styles.navButton}
              />
            )}
          </div>
        )}

        <div className={styles.focusRing} />

        {/* Scroll bars */}
        {scrollbars !== 'horizontal' ? (
          <ScrollAreaPrimitive.Scrollbar
            data-radius={4}
            orientation="vertical"
            className={clsx(
              styles.scrollbar,
              visibility === 'none' && styles.hidden,
            )}
          >
            <ScrollAreaPrimitive.Thumb
              className={clsx(
                styles.thumb,
                visibility === 'none' && styles.hidden,
              )}
            />
          </ScrollAreaPrimitive.Scrollbar>
        ) : null}

        {scrollbars !== 'vertical' ? (
          <ScrollAreaPrimitive.Scrollbar
            data-radius={4}
            orientation="horizontal"
            className={clsx(
              styles.scrollbar,
              visibility === 'none' && styles.hidden,
            )}
          >
            <ScrollAreaPrimitive.Thumb
              className={clsx(
                styles.thumb,
                visibility === 'none' && styles.hidden,
              )}
            />
          </ScrollAreaPrimitive.Scrollbar>
        ) : null}

        {scrollbars === 'both' ? (
          <ScrollAreaPrimitive.Corner
            className={clsx(
              styles.corner,
              visibility === 'none' && styles.hidden,
            )}
          />
        ) : null}
      </ScrollAreaPrimitive.Root>
    )

    return scrollArea
  },
)
ScrollArea.displayName = 'ScrollArea'

export { ScrollArea }
export type { ScrollAreaProps }
